import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { type PublicFolder, isShareSlug } from '@xfile/shared';
import { useI18n } from '../i18n';
import { ClockIcon, ExclamationTriangleIcon, FolderIcon } from '../storage/icons';
import { PublicChrome, PublicNotice } from './PublicChrome';
import { PublicFolderContents } from './PublicFolderContents';
import { type PublicResolution, fetchPublicFolder } from './share-api';

/**
 * Page PUBLIQUE d'un dossier partagé (`/d/:slug`) — accessible DÉCONNECTÉ (hors `PrivateRoute`,
 * via `PublicChrome`). Le slug du dossier EST l'autorisation (aucune auth).
 *
 * Ce module ne porte que la COQUILLE de page : résolution du slug, états de résolution (chargement /
 * `404` / `410` / ÉCHEC DE VÉRIFICATION / `200`) et titre du dossier. Toute la présentation du
 * contenu — sélection + zip (total ou de la sélection), bascule Liste / Galerie, visionneuse, copie
 * du lien d'un fichier — vit dans {@link PublicFolderContents}, RÉUTILISÉ par l'écran de résultat du
 * dépôt communautaire (XFILE-34).
 *
 * ⚠ Les quatre issues sont DISTINCTES (audit v2.4.0). Le rejet de la résolution retombait autrefois
 * sur `notFound`, si bien qu'une coupure réseau d'une seconde annonçait « Lien de partage
 * introuvable » — un mensonge, sans porte de sortie, au destinataire ANONYME d'un lien qu'on vient
 * de lui envoyer. `error` porte donc son propre message ET un bouton « Réessayer », sur le modèle
 * déjà en place dans `JoinView`.
 */

type State = { status: 'loading' } | PublicResolution<PublicFolder>;

export function PublicFolderView(): React.JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  const [state, setState] = useState<State>({ status: 'loading' });
  // Compteur de tentative : le bouton « Réessayer » l'incrémente, ce qui rejoue l'effet de
  // résolution (même mécanique que `attempt` dans `JoinView`, sans dupliquer la requête).
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!isShareSlug(slug)) {
      setState({ status: 'notFound' });
      return;
    }
    let active = true;
    setState({ status: 'loading' });
    // `fetchPublicFolder` ne rejette plus (tout échec devient `error`) ; le second handler reste
    // par prudence — une exception inattendue ne doit pas laisser la page bloquée sur « Chargement ».
    fetchPublicFolder(slug).then(
      (res) => {
        if (active) setState(res);
      },
      () => {
        if (active) setState({ status: 'error' });
      },
    );
    return () => {
      active = false;
    };
  }, [slug, attempt]);

  return (
    <PublicChrome>
      <PublicFolderBody state={state} slug={slug} onRetry={retry} />
    </PublicChrome>
  );
}

function PublicFolderBody({
  state,
  slug,
  onRetry,
}: {
  state: State;
  slug: string;
  onRetry: () => void;
}): React.JSX.Element {
  const { t } = useI18n();

  if (state.status === 'loading') {
    return (
      <p role="status" className="text-muted">
        {t('public.loading')}
      </p>
    );
  }

  if (state.status === 'gone') {
    return <PublicNotice title={t('public.gone')} icon={<ClockIcon className="size-6" />} />;
  }

  if (state.status === 'notFound') {
    return (
      <PublicNotice
        title={t('public.notFound')}
        icon={<ExclamationTriangleIcon className="size-6" />}
      />
    );
  }

  // ÉCHEC DE VÉRIFICATION : on n'affirme RIEN sur le lien, on propose de retenter. `role="alert"`
  // (et non `status`) : c'est une impasse, elle doit interrompre le lecteur d'écran.
  if (state.status === 'error') {
    return (
      <section className="flex flex-col items-center gap-4 py-12 text-center" role="alert">
        <span className="text-muted">
          <ExclamationTriangleIcon className="size-6" />
        </span>
        <p className="text-lg font-medium">{t('public.error')}</p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md bg-brand-strong px-4 py-2 text-sm font-medium text-brand-fg transition-colors hover:bg-brand-strong-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
        >
          {t('public.retry')}
        </button>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start gap-3">
        <FolderIcon className="size-8 shrink-0 text-brand-ink" />
        <h1 className="min-w-0 flex-1 break-words text-xl font-semibold">{state.data.name}</h1>
      </header>
      <PublicFolderContents folder={state.data} slug={slug} />
    </section>
  );
}
